import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import AddBulkSlotsModal from '../components/AddBulkSlotsModal';
import EditUserModal from '../components/EditUserModal';
import DeleteUserModal from '../components/DeleteUserModal';
import EditBarbershopModal from '../components/EditBarbershopModal';
import AddSponsorModal from '../components/AddSponsorModal';
import { useAppContext } from '../context/AppContext';
import { getSlotsByBarbershop } from '../services/slotService';
import { getSponsors, deleteSponsor, updateSponsor } from '../services/sponsorService';
import { deleteUserAccount } from '../services/userService';
import { AUDIT_ACTIONS, createAuditLog } from '../services/auditService';
import toast from 'react-hot-toast';

/**
 * Página: AdminPage (/pages/AdminPage.jsx)
 * Página de administração do sistema
 */
const AdminPage = () => {
  const navigate = useNavigate();
  const { currentUser, barbershops, users, addBarbershop, addSlot, fetchAllUsers } = useAppContext();
  const [tab, setTab] = useState('shops'); // 'shops' | 'schedule' | 'users' | 'sponsors' | 'logs'
  const [selectedBarbershop, setSelectedBarbershop] = useState(null);
  const [barbershopSlots, setBarbershopSlots] = useState([]);
  const [isAddSlotModalOpen, setIsAddSlotModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [isEditBarbershopModalOpen, setIsEditBarbershopModalOpen] = useState(false);
  const [isAddSponsorModalOpen, setIsAddSponsorModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBarbershopForEdit, setSelectedBarbershopForEdit] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  
  // --- Estados para o formulário de Barbearia ---
  const [shopName, setShopName] = useState('');
  const [shopBanner, setShopBanner] = useState('');
  const [shopDesc, setShopDesc] = useState('');
  const [isSponsored, setIsSponsored] = useState(false);

  // Carregar usuários quando a aba de usuários for aberta
  useEffect(() => {
    if (tab === 'users') {
      fetchAllUsers();
    }
  }, [tab, fetchAllUsers]);

  // Carregar patrocínios quando a aba de sponsors for aberta
  useEffect(() => {
    if (tab === 'sponsors') {
      loadSponsors();
    }
  }, [tab]);

  // Carregar slots quando uma barbearia for selecionada
  useEffect(() => {
    const loadSlots = async () => {
      if (selectedBarbershop?.id) {
        const result = await getSlotsByBarbershop(selectedBarbershop.id);
        if (result.success) {
          setBarbershopSlots(result.data);
        } else {
          setBarbershopSlots([]);
        }
      } else {
        setBarbershopSlots([]);
      }
    };
    
    loadSlots();
  }, [selectedBarbershop]);

  const loadSponsors = async () => {
    const result = await getSponsors(false); // Carregar todos, ativos e inativos
    if (result.success) {
      setSponsors(result.data);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditUserModalOpen(false);
    setSelectedUser(null);
  };

  const handleOpenDeleteUserModal = (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Você não pode excluir sua própria conta pelo painel administrativo.');
      return;
    }
    setSelectedUser(user);
    setIsDeleteUserModalOpen(true);
  };

  const handleCloseDeleteUserModal = () => {
    if (isDeletingUser) return;
    setIsDeleteUserModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser?.id || isDeletingUser) return;

    setIsDeletingUser(true);
    const userToDelete = selectedUser;
    try {
      const result = await deleteUserAccount(userToDelete.id);
      if (!result.success) {
        toast.error(result.error || 'Erro ao excluir usuário');
        return;
      }

      await createAuditLog({
        action: AUDIT_ACTIONS.DELETE_USER,
        userId: currentUser.id,
        userName: currentUser.name || currentUser.email,
        userEmail: currentUser.email,
        userRole: currentUser.role,
        targetType: 'user',
        targetId: userToDelete.id,
        targetName: userToDelete.name || userToDelete.email,
        description: `Usuário ${userToDelete.name || userToDelete.email} excluído pelo administrador`,
        details: { deletedUserEmail: userToDelete.email, deletedUserRole: userToDelete.role }
      });

      setIsDeleteUserModalOpen(false);
      setSelectedUser(null);
      await fetchAllUsers();
      toast.success('Usuário excluído com sucesso!');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erro inesperado ao excluir usuário');
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleEditBarbershop = (shop) => {
    setSelectedBarbershopForEdit(shop);
    setIsEditBarbershopModalOpen(true);
  };

  const handleCloseEditBarbershopModal = () => {
    setIsEditBarbershopModalOpen(false);
    setSelectedBarbershopForEdit(null);
  };

  const handleBarbershopUpdateSuccess = () => {
    // Recarregar lista de barbearias (Context já faz isso automaticamente)
    toast.success('Lista de barbearias atualizada!');
  };

  const handleToggleSponsorStatus = async (sponsorId, currentStatus) => {
    const result = await updateSponsor(sponsorId, { isActive: !currentStatus });
    if (result.success) {
      toast.success(`Patrocínio ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      loadSponsors();
    } else {
      toast.error(result.error || 'Erro ao atualizar status');
    }
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm('Tem certeza que deseja deletar este patrocínio?')) {
      return;
    }

    const result = await deleteSponsor(sponsorId);
    if (result.success) {
      toast.success('Patrocínio deletado com sucesso!');
      loadSponsors();
    } else {
      toast.error(result.error || 'Erro ao deletar patrocínio');
    }
  };

  const handleSponsorSuccess = () => {
    loadSponsors();
  };

  const handleCreateShop = async (e) => {
    e.preventDefault();
    const newShop = {
      name: shopName,
      description: shopDesc,
      bannerUrl: shopBanner || 'https://placehold.co/1200x400/333333/ffffff?text=Nova+Barbearia',
      isSponsored: isSponsored,
      city: 'Cidade (Editar)',
      rating: 0,
      services: ['Corte Masculino', 'Barba'],
    };
    
    const result = await addBarbershop(newShop);
    
    if (result?.success) {
      // Reset form
      setShopName('');
      setShopBanner('');
      setShopDesc('');
      setIsSponsored(false);
      toast.success('Barbearia criada com sucesso!');
    }
  };

  const handleAddSlot = async (slotsArray) => {
    if (!selectedBarbershop) {
      toast.error('Selecione uma barbearia primeiro');
      return;
    }

    // Adicionar barbershopId a cada slot
    const slotsWithBarbershop = slotsArray.map(slot => ({
      ...slot,
      barbershopId: selectedBarbershop.id,
    }));

    // Adicionar todos os slots
    let successCount = 0;
    for (const slotData of slotsWithBarbershop) {
      const result = await addSlot(slotData);
      if (result?.success) {
        successCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} horário(s) adicionado(s) com sucesso!`);
      setIsAddSlotModalOpen(false);
      
      // Recarregar slots da barbearia
      const slotsResult = await getSlotsByBarbershop(selectedBarbershop.id);
      if (slotsResult.success) {
        setBarbershopSlots(slotsResult.data);
      }
    } else {
      toast.error('Erro ao adicionar horários');
    }
  };

  const TabButton = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base ${
        active
          ? 'border-b-2 border-indigo-600 text-indigo-600'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Painel do Administrador</h1>
        
        {/* Navegação em Abas */}
        <div className="border-b border-gray-300 mb-8 overflow-x-auto">
          <nav className="flex space-x-2 md:space-x-4 min-w-max md:min-w-0">
            <TabButton active={tab === 'shops'} onClick={() => setTab('shops')}>
              {icons.briefcase({ className: 'h-5 w-5 inline mr-2' })}
              <span className="hidden sm:inline">Gerenciar </span>Barbearias
            </TabButton>
            <TabButton active={tab === 'schedule'} onClick={() => setTab('schedule')}>
              {icons.calendar({ className: 'h-5 w-5 inline mr-2' })}
              <span className="hidden sm:inline">Cadastrar </span>Horários
            </TabButton>
            <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
              {icons.user({ className: 'h-5 w-5 inline mr-2' })}
              <span className="hidden sm:inline">Gerenciar </span>Usuários
            </TabButton>
            <TabButton active={tab === 'sponsors'} onClick={() => setTab('sponsors')}>
              {icons.star({ className: 'h-5 w-5 inline mr-2' })}
              <span className="hidden sm:inline">Gerenciar </span>Patrocínios
            </TabButton>
            <TabButton active={tab === 'logs'} onClick={() => navigate('/admin/logs')}>
              {icons.shield({ className: 'h-5 w-5 inline mr-2' })}
              <span className="hidden sm:inline">Logs de </span>Auditoria
            </TabButton>
          </nav>
        </div>

        {/* Conteúdo das Abas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* Coluna de Ação (Formulários) */}
          <div className="md:col-span-1">
            {tab === 'shops' && (
              <form onSubmit={handleCreateShop} className="bg-white rounded-xl shadow-xl p-6 sticky top-24 space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Criar Nova Barbearia</h3>
                <div>
                  <label htmlFor="shopName" className="block text-sm font-medium text-gray-700">Nome</label>
                  <input type="text" id="shopName" value={shopName} onChange={e => setShopName(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2" required />
                </div>
                <div>
                  <label htmlFor="shopBanner" className="block text-sm font-medium text-gray-700">URL do Banner</label>
                  <input type="text" id="shopBanner" value={shopBanner} onChange={e => setShopBanner(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2" />
                </div>
                <div>
                  <label htmlFor="shopDesc" className="block text-sm font-medium text-gray-700">Descrição</label>
                  <textarea id="shopDesc" value={shopDesc} onChange={e => setShopDesc(e.target.value)} rows="3" className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2"></textarea>
                </div>
                <div className="flex items-center">
                  <input id="isSponsored" type="checkbox" checked={isSponsored} onChange={e => setIsSponsored(e.target.checked)} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                  <label htmlFor="isSponsored" className="ml-2 block text-sm font-medium text-gray-700">Patrocinada?</label>
                </div>
                <Button type="submit" variant="primary" className="w-full">
                  {icons.plus({ className: 'h-5 w-5 mr-2' })}
                  Criar Barbearia
                </Button>
              </form>
            )}
            {tab === 'schedule' && (
              <div className="bg-white rounded-xl shadow-xl p-6 sticky top-24 space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Cadastrar Horários</h3>
                {selectedBarbershop ? (
                  <>
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <p className="text-sm text-gray-600">Barbearia selecionada:</p>
                      <p className="font-semibold text-gray-900">{selectedBarbershop.name}</p>
                    </div>
                    <Button 
                      variant="primary" 
                      className="w-full" 
                      onClick={() => setIsAddSlotModalOpen(true)}
                    >
                      {icons.plus({ className: 'h-5 w-5 mr-2' })}
                      Adicionar Horário
                    </Button>
                    <Button 
                      variant="secondary" 
                      className="w-full" 
                      onClick={() => setSelectedBarbershop(null)}
                    >
                      Trocar Barbearia
                    </Button>
                  </>
                ) : (
                  <p className="text-gray-600">Selecione uma barbearia na lista ao lado para gerenciar seus horários.</p>
                )}
              </div>
            )}
          </div>
          
          {/* Coluna de Lista (Conteúdo) */}
          <div className="md:col-span-2 bg-white rounded-xl shadow-xl p-6 md:p-8">
            {tab === 'shops' && (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Barbearias Cadastradas</h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {barbershops.map(shop => (
                    <div key={shop.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <span className="font-semibold text-gray-800">{shop.name}</span>
                        {shop.isSponsored && <span className="ml-2 text-xs font-bold text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full">Patrocinada</span>}
                      </div>
                      <Button variant="secondary" className="py-1 px-3 text-sm" onClick={() => handleEditBarbershop(shop)}>
                        {icons.pencil({ className: 'h-4 w-4 mr-1' })}
                        Editar
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
            {tab === 'schedule' && (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {selectedBarbershop ? 'Gerenciar Horários' : 'Selecione uma Barbearia'}
                </h3>
                {selectedBarbershop ? (
                  <div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Barbearia selecionada:</p>
                          <p className="text-xl font-bold text-gray-900">{selectedBarbershop.name}</p>
                        </div>
                        <button
                          onClick={() => setSelectedBarbershop(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {icons.x({ className: 'h-5 w-5' })}
                        </button>
                      </div>
                    </div>

                    {/* Lista de Horários */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">
                          Horários Cadastrados ({barbershopSlots.filter(s => !s.isBooked).length})
                        </h4>
                      </div>
                      
                      {barbershopSlots.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-lg">
                          <p className="text-gray-500">Nenhum horário cadastrado ainda</p>
                          <p className="text-sm text-gray-400 mt-1">Use o painel ao lado para adicionar</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {barbershopSlots
                            .filter(slot => !slot.isBooked)
                            .sort((a, b) => {
                              if (a.date === b.date) return a.time.localeCompare(b.time);
                              return a.date.localeCompare(b.date);
                            })
                            .map(slot => (
                              <div key={slot.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors">
                                <div className="flex items-center gap-3">
                                  <div className="bg-indigo-100 p-2 rounded-lg">
                                    {icons.calendar({ className: 'h-5 w-5 text-indigo-600' })}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {new Date(slot.date + 'T00:00:00').toLocaleDateString('pt-BR', { 
                                        day: '2-digit', 
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {icons.clock({ className: 'h-4 w-4 inline mr-1' })}
                                      {slot.time}
                                    </p>
                                  </div>
                                </div>
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                  Disponível
                                </span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {barbershops.map(shop => (
                      <button
                        key={shop.id}
                        onClick={() => setSelectedBarbershop(shop)}
                        className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left"
                      >
                        <div>
                          <span className="font-semibold text-gray-800 block">{shop.name}</span>
                          <span className="text-sm text-gray-500">{shop.city}</span>
                        </div>
                        <div className="text-indigo-600">
                          {icons.chevronRight({ className: 'h-5 w-5' })}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {tab === 'users' && (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Lista de Usuários
                </h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {users.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>Nenhum usuário encontrado</p>
                    </div>
                  ) : (
                    users.map(user => (
                      <div key={user.id} className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 transition-all">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900">{user.name || 'Sem nome'}</h4>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                user.role === 'owner' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {user.role === 'admin' ? 'Admin' : user.role === 'owner' ? 'Dono' : 'Usuário'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">{user.email}</p>
                            {user.phone && (
                              <p className="text-sm text-gray-500">📱 {user.phone}</p>
                            )}
                            {user.role === 'owner' && (user.barbershopId || (user.barbershops && user.barbershops[0])) && (
                              <p className="text-xs text-indigo-600 mt-2">
                                🏪 Barbearia: {barbershops.find(b => b.id === (user.barbershopId || user.barbershops[0]))?.name || user.barbershopId || user.barbershops[0]}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 ml-4">
                            <Button
                              variant="secondary"
                              className="py-1 px-3 text-sm"
                              onClick={() => handleEditUser(user)}
                            >
                              {icons.pencil({ className: 'h-4 w-4 inline mr-1' })}
                              Editar
                            </Button>
                            <Button
                              variant="danger"
                              className="py-1 px-3 text-sm"
                              onClick={() => handleOpenDeleteUserModal(user)}
                              disabled={user.id === currentUser?.id}
                              title={user.id === currentUser?.id ? 'Não é possível excluir a conta em uso' : 'Excluir usuário'}
                            >
                              {icons.trash({ className: 'h-4 w-4 inline mr-1' })}
                              Excluir
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
            {tab === 'sponsors' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Lista de Patrocínios
                  </h3>
                  <Button
                    onClick={() => setIsAddSponsorModalOpen(true)}
                    variant="primary"
                    className="py-2 px-4 text-sm"
                  >
                    {icons.plus({ className: 'h-4 w-4 inline mr-1' })}
                    Novo Patrocínio
                  </Button>
                </div>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {sponsors.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>Nenhum patrocínio encontrado</p>
                      <button
                        onClick={() => setIsAddSponsorModalOpen(true)}
                        className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Criar primeiro patrocínio
                      </button>
                    </div>
                  ) : (
                    sponsors.map(sponsor => (
                      <div key={sponsor.id} className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 transition-all">
                        <div className="flex items-start gap-4">
                          {/* Thumbnail da imagem */}
                          <img
                            src={sponsor.imageUrl}
                            alt={sponsor.name}
                            className="w-32 h-20 object-cover rounded-lg"
                          />
                          
                          {/* Informações */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900">{sponsor.name}</h4>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                sponsor.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {sponsor.isActive ? 'Ativo' : 'Inativo'}
                              </span>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                Prioridade: {sponsor.priority}
                              </span>
                            </div>
                            
                            {sponsor.description && (
                              <p className="text-sm text-gray-600 mb-2">{sponsor.description}</p>
                            )}
                            
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>Tipo: {sponsor.type === 'banner' ? 'Banner Geral' : 'Barbearia Específica'}</span>
                              <span>Expira: {new Date(sponsor.expiresAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                            </div>
                          </div>
                          
                          {/* Ações */}
                          <div className="flex flex-col gap-2">
                            <Button
                              variant={sponsor.isActive ? 'secondary' : 'primary'}
                              className="py-1 px-3 text-sm"
                              onClick={() => handleToggleSponsorStatus(sponsor.id, sponsor.isActive)}
                            >
                              {sponsor.isActive ? 'Desativar' : 'Ativar'}
                            </Button>
                            <Button
                              variant="danger"
                              className="py-1 px-3 text-sm"
                              onClick={() => handleDeleteSponsor(sponsor.id)}
                            >
                              {icons.trash({ className: 'h-4 w-4 inline mr-1' })}
                              Deletar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal para Adicionar Horários em Lote */}
      <AddBulkSlotsModal
        isOpen={isAddSlotModalOpen}
        onClose={() => setIsAddSlotModalOpen(false)}
        onSubmit={handleAddSlot}
      />

      {/* Modal para Editar Usuário */}
      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={handleCloseEditModal}
        user={selectedUser}
      />

      <DeleteUserModal
        isOpen={isDeleteUserModalOpen}
        onClose={handleCloseDeleteUserModal}
        user={selectedUser}
        onConfirm={handleDeleteUser}
        isDeleting={isDeletingUser}
      />

      {/* Modal para Editar Barbearia */}
      <EditBarbershopModal
        isOpen={isEditBarbershopModalOpen}
        onClose={handleCloseEditBarbershopModal}
        barbershop={selectedBarbershopForEdit}
        onSuccess={handleBarbershopUpdateSuccess}
      />

      {/* Modal para Adicionar Patrocínio */}
      <AddSponsorModal
        isOpen={isAddSponsorModalOpen}
        onClose={() => setIsAddSponsorModalOpen(false)}
        onSuccess={handleSponsorSuccess}
      />
    </div>
  );
};

export default AdminPage;
